import React from 'react';
import PropTypes from 'prop-types';
import PictureDaySignup from '../PictureDaySignup';
import styles from './BeforePictureDay.module.scss';

// The entry screen for a school whose fall picture day HAS NOT HAPPENED YET.
//
// WHO THIS IS FOR, which is the thing an earlier design got wrong. Nearly
// everyone who reaches this page is a current-season family wanting THIS
// season's pictures, and many arrive conditioned by other companies
// (Lifetouch) to prepay online before picture day. We do not sell pre-orders --
// photos are taken, then families are texted and emailed a link -- so the job
// of this screen is to say what will actually happen and take their contact
// details, not to offer them a box to type a code into.
//
// So there are NO access-code fields here. The screen that has them is the one
// this school gets AFTER its picture day. The parent holding a code from an
// earlier shoot is a very small minority, and this screen is deliberately not
// where they are served.
//
// Dates are formatted here rather than server-side because the page is
// bilingual and a month name has to follow the reader's language.
const STRINGS = {
  en: {
    heading: 'Pictures will be taken on',
    // TWO STRINGS, broken deliberately rather than left to wrap. As one
    // sentence pair it broke wherever the viewport happened to run out, which
    // on a phone stranded "to do in advance." alone on a second line. Split
    // here so the break lands between the two thoughts at every width.
    reassure: 'Every student will be photographed.',
    reassureNote: 'Nothing to do in advance.',
    // A STATEMENT, not an instruction. "See their actual photos before you
    // order" read as a prompt -- one more thing being asked of the parent -- when
    // the job of this line is the opposite: to answer "am I buying blind?"
    // before they think to ask. So it says what WILL happen, with the parent as
    // the beneficiary rather than the actor.
    //
    // Also kept to ONE line on a 320px phone, which is what rules out the
    // longer "You'll be able to view..." phrasings.
    preview: 'You’ll be able to view pictures online before purchase.',
    offer: (terms, date) => `When your photos are ready you’ll have until ${date} to receive ${terms}.`,
    percent: (n) => `${n}% off`,
    amount: (n) => `$${n} off`,
    freeShipping: 'free shipping',
    freeShippingOver: (n) => `free shipping on orders over $${n}`,
    and: ' and ',
  },
  es: {
    heading: 'Las fotos se tomarán el',
    reassure: 'Todos los estudiantes serán fotografiados.',
    reassureNote: 'No hay que hacer nada por adelantado.',
    // "Verá" (future indicative) not "Vea" (imperative) -- the English is a
    // statement about what will happen, and an imperative here would reintroduce
    // exactly the prompt-like reading being avoided.
    preview: 'Podrá ver las fotos en línea antes de comprar.',
    offer: (terms, date) => `Cuando sus fotos estén listas tendrá hasta el ${date} para recibir ${terms}.`,
    percent: (n) => `${n}% de descuento`,
    amount: (n) => `$${n} de descuento`,
    freeShipping: 'envío gratis',
    freeShippingOver: (n) => `envío gratis en pedidos de más de $${n}`,
    and: ' y ',
  },
};

// An ISO date names a CALENDAR DAY. Splitting it by hand rather than letting
// `new Date('2026-09-11')` parse it, because that reads as UTC midnight and
// renders as the 10th for every reader west of Greenwich -- which is all of
// them. The same off-by-one this app has already shipped once server-side.
export const formatDate = (iso, lang) => {
  if (!iso) return '';
  const [year, month, day] = iso.split('-').map(Number);

  return new Date(year, month - 1, day).toLocaleDateString(lang === 'es' ? 'es-US' : 'en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
};

// Composed from the coupon's structure rather than a stored sentence, so it
// follows the reader's language and cannot promise terms the coupon does not
// give. Renders nothing when there is nothing to promise.
export const offerTerms = (offer, t) => {
  const parts = [];
  if (offer.percentOff) parts.push(t.percent(offer.percentOff));
  if (offer.amountOff) parts.push(t.amount(offer.amountOff));
  // A threshold offer names its minimum, or it would promise free shipping on
  // every order; the strings carry a fallback for a server that sent none.
  if (offer.freeShipping && offer.freeShippingMinimum && t.freeShippingOver) {
    parts.push(t.freeShippingOver(offer.freeShippingMinimum));
  } else if (offer.freeShipping) {
    parts.push(t.freeShipping);
  }

  return parts.join(t.and);
};

const BeforePictureDay = ({
  pictureDays, schoolId, lang, onSignedUp,
}) => {
  const t = STRINGS[lang] || STRINGS.en;
  const offer = pictureDays?.offer;
  const terms = offer ? offerTerms(offer, t) : '';

  return (
    <div className={styles.plan}>
      {/* The whole announcement in ONE blue block: the date, and the two lines
          that say what it means for the reader. They were siblings below it,
          which made the date look like a fact on its own and the reassurance
          like unrelated small print -- together they read as one statement a
          parent can take in at a glance. */}
      <div className={styles.dateBlock}>
        <p className={styles.heading}>{t.heading}</p>
        {/* The one thing a parent came for, set large enough to survive a
            glance at arm's length on a phone. */}
        <p className={styles.date}>{formatDate(pictureDays?.fallPictureDay, lang)}</p>

        <div className={styles.pitch}>
          {/* One paragraph, one FORCED break. Two <p>s would let the card's
              spacing get between two halves of a single thought, and leaving it
              to wrap is what stranded "to do in advance." on its own line. */}
          <p className={styles.reassure}>
            {t.reassure}
            <br />
            {t.reassureNote}
          </p>
          <p className={styles.preview}>{t.preview}</p>
        </div>
      </div>

      {/* The confirmation is handed up: the whole card becomes a thank-you
          rather than a line under a form the parent has finished with. */}
      {/* collectName: this screen has nowhere else to ask, and a signup with no
          name can never be matched when the roster arrives -- it reaches
          photos-are-ready day as an "identify your child" message instead of
          the child's photos and access code. */}
      <PictureDaySignup
        schoolId={schoolId}
        lang={lang}
        confirmInline={false}
        collectName
        onSignedUp={onSignedUp}
      />

      {/* Only when the coupon actually answered. An offer line with no
          deadline, or terms we cannot state, is a promise checkout may not
          honour -- so it is omitted rather than softened. */}
      {offer && terms && (
        <p className={styles.offer}>
          {t.offer(terms, formatDate(offer.expiresOn, lang))}
        </p>
      )}
    </div>
  );
};

BeforePictureDay.propTypes = {
  pictureDays: PropTypes.shape({
    fallPictureDay: PropTypes.string,
    offer: PropTypes.shape({
      expiresOn: PropTypes.string,
      percentOff: PropTypes.number,
      amountOff: PropTypes.number,
      freeShipping: PropTypes.bool,
      freeShippingMinimum: PropTypes.number,
    }),
  }),
  schoolId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  lang: PropTypes.string,
  onSignedUp: PropTypes.func,
};

BeforePictureDay.defaultProps = { pictureDays: null, lang: 'en', onSignedUp: null };

export default BeforePictureDay;
